import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  Table,
  Tag,
  Form,
  DatePicker,
  Input,
  Select,
  Button,
} from "antd";
import { getOrders } from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import {
  transactionTypes,
  UserType,
  transactionOrderSource,
  TransactionOrderSource,
  TransactionStatus,
} from "../../../configs/constants";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { handleApiError } from "../../../utils/errorHandler";
import { getTableRowNumber } from "../../../utils/tablePagination";

const PendingOrders = ({ branchesData, isAllData }) => {
  const navigate = useNavigate();
  const { authUser, appConfig } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [ordersTableData, setOrdersTableData] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState([]);
  const [searchFormRef] = Form.useForm();
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;

  useEffect(() => {
    setIsDataLoading(true);
    getOrders({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
      status: TransactionStatus.Pending,
      is_all_data: isAllData,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setOrdersTableData(response.data.data.transactions);
        setPaginationData(response.data.data.pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, perPageSize, isAllData, t]);

  // firstPartCol Columns
  const firstPartCol = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{getTableRowNumber(index, paginationData)}</>;
      },
    },
    {
      title: t("order_id"),
      dataIndex: "order_id",
      render: (order_id) => {
        return order_id ? order_id : "-";
      },
    },
    {
      title: t("amount"),
      render: (record) => {
        return (
          <>
            {record.is_multipayment ? (
              record.multipayments.map((item, i) => {
                return (
                  <p key={i}>
                    {item.transaction_type}: {item.amount}
                  </p>
                );
              })
            ) : (
              <p>
                <CurrencyFormatter value={record.amount_charged} />
              </p>
            )}
          </>
        );
      },
    },
    {
      title: t("source"),
      render: (record) => {
        return (
          <div className="app-flex-wrap-center">
            {Number(record.order_source?.value) ===
              TransactionOrderSource.Pos && (
              <img
                src="/icons/pos.svg"
                alt={t("pos")}
                className="app-order-meta-icon"
              />
            )}
            {Number(record.order_source?.value) ===
              TransactionOrderSource.QrOrder &&
              (record.is_drive_thru ? (
                <img
                  src="/icons/car.svg"
                  alt={t("qr")}
                  className="app-order-meta-icon"
                />
              ) : (
                <img
                  src="/icons/qr.svg"
                  alt={t("qr")}
                  className="app-order-meta-icon"
                />
              ))}
            {Number(record.order_source?.value) ===
              TransactionOrderSource.Kiosk && (
              <img
                src="/icons/kiosk.svg"
                alt={t("kiosk")}
                className="app-order-meta-icon"
              />
            )}
            {record.dining_table_name && (
              <Tag color="blue">{record.dining_table_name}</Tag>
            )}
            {record.order_type?.name && (
              <Tag color="pink">{record.order_type?.name}</Tag>
            )}
          </div>
        );
      },
    },
  ];
  // Branch Columns
  const branchCol = [
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => {
        return <p>{branch}</p>;
      },
    },
  ];
  // secondPartCol Columns
  const secondPartCol = [
    {
      title: t("created_at"),
      render: (record) => {
        return <p>{moment(record.created_at).format("MMM. D, YYYY hh:mm a")}</p>;
      },
    },
    {
      title: t("action"),
      width: "130px",
      render: (record) => {
        return (
          <Button
            type="primary"
            onClick={() => {
              navigate("/orders/" + record.id);
            }}
          >
            {t("preview")}
          </Button>
        );
      },
    },
  ];

  const handleSearch = (values) => {
    let search_params = false;
    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });

      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }
    if (values.type) {
      search_params = {
        type: values.type,
        ...search_params,
      };
    }
    if (values.order_source) {
      search_params = {
        order_source: values.order_source,
        ...search_params,
      };
    }
    if (values.filter_by_branch) {
      search_params = {
        filter_by_branch: values.filter_by_branch,
        ...search_params,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };

  const handleReset = () => {
    // Reset Form Fields
    searchFormRef.resetFields();
    // Reset Transactions List
    setSearchParams([]);
  };

  const searchForm = (
    <>
      <Form
        name="search-form"
        layout="inline"
        onFinish={handleSearch}
        form={searchFormRef}
      >
        <Form.Item name="dates" label={t("date_range")}>
          <RangePicker allowClear format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="filter_by_branch" label={t("branch")}>
          <Select
            allowClear
            className="app-w-100px"
            options={branchesData.map((branch) => {
              return {
                label: branch.name,
                value: branch.name,
              };
            })}
          />
        </Form.Item>
        <Form.Item name="type" label={t("type")}>
          <Select
            allowClear
            className="app-w-100px"
            options={transactionTypes.map((type) => {
              return {
                label: t(type.translationKey),
                value: type.id,
              };
            })}
          />
        </Form.Item>

        <Form.Item name="order_source" label={t("source")}>
          <Select
            allowClear
            className="app-w-100px"
            options={transactionOrderSource.map((type) => {
              return {
                label: t(type.translationKey),
                value: type.id,
              };
            })}
          />
        </Form.Item>

        <Form.Item>
          <Input
            type="submit"
            value={t("search")}
            className="app-search-submit"
          />
        </Form.Item>
        <Form.Item>
          <Input type="button" value={t("reset")} onClick={handleReset} />
        </Form.Item>
      </Form>
    </>
  );

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  return (
    <>
      <Card
        style={{
          direction: appConfig.direction
            ? appConfig.direction === "ltr"
              ? "ltr"
              : "rtl"
            : "ltr",
        }}
      >
        {searchForm}
      </Card>
      <Card>
        <Table
          columns={
            authUser.type === UserType.BusinessOwner
              ? [...firstPartCol, ...branchCol, ...secondPartCol]
              : [...firstPartCol, ...secondPartCol]
          }
          dataSource={ordersTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </>
  );
};

export default PendingOrders;
